import React, { useState, useEffect, useRef } from 'react';
import { Link, Typography } from '@one-thd/sui-atomic-components';
import { pushErrorEvent, setOverlayEvent } from '../../../utils/analytics';
import { setOtherButtonSubmissionAnalytics, setSuccessAuthorizationAnalytics, } from '../../../utils/payments-analytics';
import { CARD_PHONE_AUTH_AND_T2C_MSG, CARD_PHONE_AUTH_MSG, CARD_T2C_MSG, CHANGES_SAVED_MSG, PRO_ALLOW_REMOTE_PURCHASES_SUCCESS_MSG_ONE, PRO_ALLOW_REMOTE_PURCHASES_SUCCESS_MSG_TWO, STOPPED_CARD_ALL_AUTH_MSG, STOPPED_CARD_PHONE_AUTH_MSG, STOPPED_CARD_T2C_MSG, } from '../../../constants/response-success-messages';
import { usePayments } from '../../../hooks/use-payments';
import { Spinner } from '../../shared/spinner/spinner';
import { TAlertStatus } from '../../../models/common/alert-messages';
import { useCustomerInfo } from '../../../hooks/use-customerInfo';
import { useModal } from '../../../hooks/use-modal';
import { StopRemotePurchaseModal } from './stop-remote-purchases-modal';
import { getPaymentModalsVersion, getSiteKey, scrollToTop, } from '../../../utils/general';
import { T2CChangeConfirmationModal } from './t2c-change-confirmation';
import { useLoyaltyMembershipDetails } from '../../../hooks/use-loyalty-membership-details';
import { MANAGE_PRO_ALLOWANCE_PATH } from '../../../constants/links';
import { useSearchParam } from '../../../hooks/use-search-param';
import { Alert } from '../../shared/alert/alert';
import { UnenrollT2CConfirmModalBodyTextCc, UnenrollT2CConfirmModalHeaderTextCc, } from '../../../constants/credit-card';
import { usePhaUnenrollT2cConfirmModal } from '../../../hooks/use-pha-unenroll-t2c-confirm-modal';
import { ERR_GENERIC } from '../../../constants/response-error-messages';
export const RemotePurchases = ({ isConfigurableCardsEnabled, card, onCloseForm, fromEditCard, }) => {
    const { role } = useCustomerInfo();
    const paymentModalsVersion = getPaymentModalsVersion();
    const iframeSrc = `/account/payment-ui/${paymentModalsVersion}/dist/client/remote-purchases-auth.html`;
    // local dev url
    // const iframeSrc = '/modals/remote-purchases-auth.html';
    const isProxCard = card.cardBrand === 'PROX';
    const t2cPrimary = card.t2cPrimary || false;
    const { setAlertMessage, setProAllowanceMessage } = usePayments();
    const fromProAllowance = useSearchParam('fromProAllowance') === 'true';
    const { hasProAllowance: isPartOfRunnerAllowance, hasHDWallet: isPartOfHDWallet, } = useLoyaltyMembershipDetails();
    const iframeRef = useRef(null);
    const [isIframeLoading, setIsIframeLoading] = useState(true);
    const { openModal, hideModal, setComponentProps } = useModal();
    const sendDataToIframe = (data) => {
        var _a, _b;
        (_b = (_a = iframeRef.current) === null || _a === void 0 ? void 0 : _a.contentWindow) === null || _b === void 0 ? void 0 : _b.postMessage(data, '*');
    };
    const [openConfirmModal] = usePhaUnenrollT2cConfirmModal({
        selectedCard: card,
    });
    useEffect(() => {
        // remote purchases operations only
        const handleIFrameMessages = (event) => {
            var _a, _b;
            if (event.origin.indexOf('homedepotdev.com') > -1 ||
                event.origin.indexOf('homedepot.com') > -1) {
                const data = event.data;
                if (data.event === 'loaded') {
                    sendDataToIframe({
                        event: 'experience data',
                        experienceProps: window === null || window === void 0 ? void 0 : window.__EXPERIENCE_PROPS__,
                        experienceContext: window === null || window === void 0 ? void 0 : window.__EXPERIENCE_CONTEXT__,
                        data: {
                            siteKey: getSiteKey(true),
                            role,
                            features: {
                                isConfigurableCardsEnabled,
                            },
                            fromEditCard,
                        },
                    });
                    sendDataToIframe({ event: 'card', data: card });
                    setIsIframeLoading(false);
                }
                if (data.event === 'hide') {
                    onCloseForm();
                }
                if (data.event === 'exitFailure') {
                    scrollToTop();
                    setAlertMessage({
                        paymentId: card.paymentId,
                        alertStatus: TAlertStatus.ERROR,
                        text: ERR_GENERIC.message,
                    });
                    onCloseForm();
                }
                if (data.event === 'success') {
                    scrollToTop();
                    const getAlertSuccessMessage = (action) => {
                        switch (action) {
                            case 'REMOVE_PHONE_AUTH':
                            case 'REMOVE_PHONE_AUTH_AND_T2C':
                                return isProxCard
                                    ? STOPPED_CARD_PHONE_AUTH_MSG
                                    : STOPPED_CARD_ALL_AUTH_MSG;
                            case 'REMOVE_T2C':
                                return STOPPED_CARD_T2C_MSG;
                            case 'PHONE_AUTH':
                                return CARD_PHONE_AUTH_MSG;
                            case 'PHONE_AUTH_AND_T2C':
                                return CARD_PHONE_AUTH_AND_T2C_MSG;
                            case 'T2C':
                                return CARD_T2C_MSG;
                            default:
                                return CHANGES_SAVED_MSG;
                        }
                    };
                    if (fromProAllowance) {
                        setProAllowanceMessage({
                            hideHDCC: false,
                            content: (React.createElement(Alert, { status: TAlertStatus.SUCCESS, onClose: () => setProAllowanceMessage(null) },
                                React.createElement(Typography, { variant: "body-base" },
                                    PRO_ALLOW_REMOTE_PURCHASES_SUCCESS_MSG_ONE.message,
                                    ' ',
                                    React.createElement(Link, { href: MANAGE_PRO_ALLOWANCE_PATH, "data-testid": "pro-allow-remote-purch-success-msg" }, PRO_ALLOW_REMOTE_PURCHASES_SUCCESS_MSG_TWO.message)),
                                ' ')),
                        });
                    }
                    else {
                        setAlertMessage({
                            paymentId: card.paymentId,
                            alertStatus: TAlertStatus.SUCCESS,
                            text: getAlertSuccessMessage(data.data).message,
                        });
                    }
                    onCloseForm();
                    setSuccessAuthorizationAnalytics(data.data, isProxCard, t2cPrimary);
                }
                if (data.event === 'failure') {
                    pushErrorEvent('remote purchases overlay', 'inline', ((_a = data.data) === null || _a === void 0 ? void 0 : _a.errorCode) || 'n/a', (_b = data.data) === null || _b === void 0 ? void 0 : _b.errorMessage);
                }
                if (data.event === 'openStopAuthModal') {
                    setOverlayEvent('stop remote purchase approvals');
                    openModal(StopRemotePurchaseModal, {
                        card,
                        onlyPhoneAuth: isProxCard,
                        onConfirmation: () => {
                            setComponentProps({ isLoading: true });
                            sendDataToIframe({ event: 'stopAuthConfirmed' });
                        },
                        hasBenefit: isPartOfRunnerAllowance,
                        isLoading: false,
                    });
                }
                if (data.event === 'unenrollT2cOnly') {
                    const confirmModalProps = {
                        bodyText: UnenrollT2CConfirmModalBodyTextCc,
                        headerText: UnenrollT2CConfirmModalHeaderTextCc,
                    };
                    openConfirmModal(confirmModalProps).then((confirmed) => {
                        if (!confirmed) {
                            onCloseForm();
                            return;
                        }
                        sendDataToIframe({ event: 'unenrollT2cOnlyConfirmed' });
                    });
                }
                if (data.event === 'closeStopAuthModal') {
                    hideModal();
                    setComponentProps({ isLoading: false });
                }
                if (data.event === 't2cConfirm' && data.data.phoneNumber) {
                    openModal(T2CChangeConfirmationModal, {
                        phoneNumber: data.data.phoneNumber,
                        hideModal: () => {
                            sendDataToIframe({ event: 't2cDenied' });
                            hideModal();
                            scrollToTop();
                        },
                        onConfirmation: () => {
                            setComponentProps({ isLoading: true });
                            sendDataToIframe({ event: 't2cConfirmed' });
                        },
                        isLoading: false,
                    });
                }
                if (data.event === 'openTooltip' && data.data) {
                    setOtherButtonSubmissionAnalytics(`credit cards: approve by ${data.data.toLowerCase()} tooltip`);
                }
            }
        };
        window.addEventListener('message', handleIFrameMessages);
        scrollToTop();
        return () => {
            window.removeEventListener('message', handleIFrameMessages);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        if (!isIframeLoading && isPartOfRunnerAllowance) {
            sendDataToIframe({
                event: 'allowanceAccess',
                data: {
                    isPartOfRunnerAllowance,
                },
            });
        }
    }, [isIframeLoading, isPartOfRunnerAllowance]);
    useEffect(() => {
        if (!isIframeLoading && isPartOfHDWallet) {
            sendDataToIframe({
                event: 'hdWalletAccess',
                data: {
                    isPartOfHDWallet,
                },
            });
        }
    }, [isIframeLoading, isPartOfHDWallet]);
    return (React.createElement("div", null,
        React.createElement(Spinner, { active: isIframeLoading }),
        React.createElement("iframe", { ref: (el) => {
                iframeRef.current = el;
            }, className: "sui-h-[1900px] sui-w-full", title: "Remote Purchases form", src: iframeSrc })));
};
